<script>
import DataCard from "../../components/overview/DataCard.vue";
import Icon from "../../components/Icon.vue";
import { useUserStore } from "../../../stores/user";
import { useReportStore } from "../../../stores/report";
import { useRoute } from "vue-router";

export default {
  components: { DataCard, Icon },
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    currentDonor: {
      type: Object,
      default: null
    }
  },
  emits: ['show-popup'],
  setup() {
    const userStore = useUserStore();
    const reportStore = useReportStore();
    const route = useRoute();
    return { userStore, reportStore, route };
  },
  data() {
    return {
      selectedYear: null
    };
  },
  computed: {
    organizationId() {
      return this.route.params.org;
    },
    donationYears() {
      return this.userStore.userDonationsYears;
    },
    hasDonations() {
      const org =
        this.userStore.organizations.find(o => o.slug === this.organizationId) ||
        this.userStore.currentOrganization;
      const orgId = org?.id;

      const filtered = (Array.isArray(this.userStore.donations) ? this.userStore.donations : [])
        .filter(d =>
          !d?.archived &&
          (!orgId || d?.organization_id === orgId) &&
          (!d?.status || ['paid','succeeded','completed','success'].includes(String(d.status).toLowerCase()))
        );
      return filtered.length > 0;
    }
  },
  watch: {
    donationYears(newYears) {
      if (!newYears.includes(this.selectedYear)) {
        this.selectedYear = null;
      }
    }
  },
  mounted() {
    this.reportStore.setOrganizationId(this.organizationId);
  },
  methods: {
    showPopup(message) {
      this.$emit('show-popup', message);
    },
    downloadContributionStatement(year = null) {
      if (!this.currentDonor?.id) {
        this.showPopup('Could not find your donor profile for this organization.');
        return;
      }

      const effectiveYear = year ?? new Date().getFullYear();
      this.showPopup('Generating your contribution statement...');

      this.reportStore.createDonorDonationReport(this.currentDonor, effectiveYear)
        .then((reportId) => {
          if (!reportId) throw new Error('Failed to create report');
          return this.reportStore.pollReport(reportId).then(() => reportId);
        })
        .then((reportId) => {
          this.showPopup('Statement downloaded successfully!');
          this.reportStore.downloadDonorReport(reportId, this.userStore.currentOrganization.slug, year);
        })
        .catch((e) => {
          console.error(e);
          this.showPopup(e?.message || 'Failed to download statement. Please try again.');
        });
    },
    downloadYearToDate() {
      this.downloadContributionStatement('ytd');
    },
    downloadSelectedYear() {
      if (!this.selectedYear) {
        this.showPopup('Please select a year first.');
        return;
      }
      this.downloadContributionStatement(this.selectedYear);
    }
  }
};
</script>

<template>
  <data-card
    :loaded="!loading"
    title="Contribution Reports"
    title-class="text-lg font-semibold"
    additional-text="Download your contributions here."
    additional-text-class="text-xs text-gray-500 font-normal"
    :left-sidebar="false"
    bordered
    border-class="border border-slate-200 rounded-lg shadow"
  >
    <div class="contrib-cta">
      <div class="contrib-cta__left">
        <div class="contrib-cta__icon">
          <icon
            icon="invoice"
            size="18px"
            color="#3a4473"
          />
        </div>
        <div>
          <div class="contrib-cta__title">
            Get your records
          </div>
          <div class="contrib-cta__subtitle">
            <template v-if="hasDonations && !loading">
              Export a YTD statement or view all past reports.
            </template>
            <template v-else>
              You don't have any donations yet.
            </template>
          </div>
        </div>
      </div>

      <!-- Only show actions when there are donations -->
      <div
        v-if="hasDonations && !loading"
        class="contrib-cta__actions"
      >
        <!-- YTD -->
        <button
          type="button"
          class="btn-brand"
          @click="downloadYearToDate()"
        >
          Download YTD
        </button>
        <button
          v-if="selectedYear"
          type="button"
          class="btn-ghost"
          @click="downloadSelectedYear"
        >
          Download {{ selectedYear }}
        </button>

        <select
          v-model="selectedYear"
          class="year-select"
          aria-label="Select year"
        >
          <option
            :value="null"
            disabled
          >
            Select year
          </option>
          <option
            v-for="y in donationYears"
            :key="y"
            :value="y"
          >
            {{ y }}
          </option>
        </select>
      </div>
    </div>
  </data-card>
</template>

<style scoped lang="scss">
</style>
